// A small Markdown reader for what people write in the panel: a plan, a tier's
// description and expected outcome, a task's description.
//
// It lives in `common/` because more than one feature reads it, and
// `features/a → features/b` is forbidden (TSS vite/01 [RIGID]).
//
// WHY IT BUILDS REACT ELEMENTS AND NEVER AN HTML STRING.
//
// This text is written by people and, through the agent surface, by a master
// agent. It is not trusted input. The usual shortcut — turn Markdown
// into an HTML string and hand it to `dangerouslySetInnerHTML` — makes the
// preview only as safe as the sanitiser somebody remembers to keep updated,
// and the failure is silent until it is not.
//
// This returns React elements. Text arrives as text children, which React
// escapes on its own, so there is NO PATH where a `<script>`, an `onerror=` or
// a `<style>` in the content becomes part of the document. There is nothing to
// sanitise because nothing is ever parsed as HTML.
//
// Links are the one place a value reaches an attribute, so the protocol is
// checked against a list: http, https and mailto. `javascript:` and `data:`
// render as plain text, keeping the words and dropping the link.
//
// It is deliberately a SUBSET — headings, lists, quotes, fenced code, bold,
// italic, inline code and links. A plan is prose with structure, not a
// document format. Anything unsupported shows as the literal text somebody
// typed, which is honest: nothing is silently dropped.

import { isSafeHref } from './links';

import './styles/markdown.css';

let keySeed = 0;
const nextKey = () => `md-${keySeed++}`;

// ------------------------------------------------------------------ inline

// Order matters: code first, so `**` inside a code span is not read as bold.
const INLINE = [
    { pattern: /`([^`]+)`/,            render: (m) => <code key={nextKey()} className="md__code">{m[1]}</code> },
    { pattern: /\*\*([^*]+)\*\*/,      render: (m) => <strong key={nextKey()}>{m[1]}</strong> },
    { pattern: /(?<!\*)\*([^*]+)\*/,   render: (m) => <em key={nextKey()}>{m[1]}</em> },
    { pattern: /\[([^\]]+)\]\(([^)\s]+)\)/, render: (m) => renderLink(m[1], m[2]) },
];

function renderLink(text, href) {
    if (!isSafeHref(href)) {
        // Keep the words, drop the link. A `javascript:` href is not an error
        // to hide — it is content that must not become clickable.
        return <span key={nextKey()}>{text}</span>;
    }
    return (
        <a
            key={nextKey()}
            href={href}
            className="md__link"
            /* noreferrer with noopener: the target must not reach back into
               this window, and must not learn where it was opened from. */
            target="_blank"
            rel="noopener noreferrer"
        >
            {text}
        </a>
    );
}

function inline(text) {
    // `[]` AND NOT `null`, and this line has a bug behind it. The recursion at
    // the bottom spreads this call's result, and spreading `null` throws — so
    // any inline construct that ended exactly at the end of its line (a link as
    // the last thing in a paragraph, which is common) crashed the preview.
    // Returning an empty list makes the empty tail a non-event.
    if (!text) return [];

    // Find the earliest match among all inline rules, emit what came before it
    // as plain text, then continue after it. Plain text stays plain text, which
    // is what React escapes for us.
    let earliest = null;
    for (const rule of INLINE) {
        const match = rule.pattern.exec(text);
        if (match && (earliest === null || match.index < earliest.match.index)) {
            earliest = { rule, match };
        }
    }

    if (!earliest) return [text];

    const { rule, match } = earliest;
    return [
        text.slice(0, match.index),
        rule.render(match),
        ...inline(text.slice(match.index + match[0].length)),
    ].filter(part => part !== '');
}

// ------------------------------------------------------------------ blocks

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET  = /^\s*[-*]\s+(.*)$/;
const NUMBER  = /^\s*\d+[.)]\s+(.*)$/;
const QUOTE   = /^>\s?(.*)$/;
const FENCE   = /^```/;
// An indented, non-blank line: how a list item continues on the next line.
const CONTINUATION = /^\s{2,}\S/;

export function renderMarkdown(source) {
    if (!source || !source.trim()) return null;

    const lines = source.replace(/\r\n/g, '\n').split('\n');
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (!line.trim()) { i += 1; continue; }

        if (FENCE.test(line)) {
            const body = [];
            i += 1;
            while (i < lines.length && !FENCE.test(lines[i])) { body.push(lines[i]); i += 1; }
            i += 1;   // the closing fence, or the end of the text
            blocks.push(<pre key={nextKey()} className="md__pre"><code>{body.join('\n')}</code></pre>);
            continue;
        }

        const heading = HEADING.exec(line);
        if (heading) {
            // Headings start at <h3>: this renders inside a page that already
            // has an <h1> for the panel and an <h2> for the plan, and a
            // document whose outline jumps to <h1> mid-page is a document a
            // screen reader cannot navigate.
            const level = Math.min(heading[1].length + 2, 6);
            const Tag = `h${level}`;
            blocks.push(<Tag key={nextKey()} className="md__heading">{inline(heading[2])}</Tag>);
            i += 1;
            continue;
        }

        if (QUOTE.test(line)) {
            const body = [];
            while (i < lines.length && QUOTE.test(lines[i])) {
                body.push(QUOTE.exec(lines[i])[1]);
                i += 1;
            }
            blocks.push(
                <blockquote key={nextKey()} className="md__quote">{inline(body.join(' '))}</blockquote>,
            );
            continue;
        }

        if (BULLET.test(line) || NUMBER.test(line)) {
            const ordered = NUMBER.test(line) && !BULLET.test(line);
            const items = [];
            while (i < lines.length && (BULLET.test(lines[i]) || NUMBER.test(lines[i]))) {
                const item = BULLET.exec(lines[i]) ?? NUMBER.exec(lines[i]);
                const text = [item[1]];
                i += 1;
                // A wrapped item: the next lines are indented and are not a
                // new marker. They belong to this item — read as a paragraph
                // of their own, a numbered point broke in two.
                while (i < lines.length && CONTINUATION.test(lines[i])
                    && !BULLET.test(lines[i]) && !NUMBER.test(lines[i])) {
                    text.push(lines[i].trim());
                    i += 1;
                }
                items.push(<li key={nextKey()} className="md__item">{inline(text.join(' '))}</li>);
            }
            const List = ordered ? 'ol' : 'ul';
            blocks.push(<List key={nextKey()} className="md__list">{items}</List>);
            continue;
        }

        // A paragraph runs until a blank line or the start of another block.
        const body = [];
        while (
            i < lines.length && lines[i].trim()
            && !HEADING.test(lines[i]) && !BULLET.test(lines[i])
            && !NUMBER.test(lines[i]) && !QUOTE.test(lines[i]) && !FENCE.test(lines[i])
        ) {
            body.push(lines[i]);
            i += 1;
        }
        blocks.push(<p key={nextKey()} className="md__paragraph">{inline(body.join(' '))}</p>);
    }

    return blocks;
}
